import axios from 'axios';
import { EncryptionServicePY } from 'Libs/utils/EncryptionProviders';

const API_ENDPOINT = 'https://pyapiv2.mintbox.ai/api/v2';

let accessToken = null;
let tokenPromise = null;

const pendingRequests = new Map();
const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

const crypto = new EncryptionServicePY("p10tibarp999sha11lynt");

const client = axios.create({
	baseURL: API_ENDPOINT,
	withCredentials: false
});

const generateRequestKey = (method, path, data) =>
	`${method}:${path}:${JSON.stringify(data || {})}`;


// ---------------- DataServicePY ----------------
class DataServicePYV2 {

	static async get(path = '', data = {}) { return this.request('GET', path, data); }
	static async post(path = '', data = {}) { return this.request('POST', path, data); }
	static async patch(path = '', data = {}) { return this.request('PATCH', path, data); }
	static async put(path = '', data = {}) { return this.request('PUT', path, data); }
	static async delete(path = '', data = {}) { return this.request('DELETE', path, data); }

	static async postImg(path = '', data = {}) {
		const token = await getAccessToken();

		const headers = {};
		if (token) headers.Authorization = `Bearer ${token}`;

		return fetch(API_ENDPOINT + path, {
			method: 'POST',
			headers,
			body: data
		}).then(async r => {
			if (!r.ok) {
				return {
					msg: 'Failed',
					data: await r.text(),
					type: false
				};
			}
			const blob = await r.blob();
			return {
				msg: 'Success',
				data: blob,
				type: true
			};
		});
	}

	static async request(method, path, data = {}) {

		const key = generateRequestKey(method, path, data);

		if (pendingRequests.has(key)) return pendingRequests.get(key);

		const optional = { 'Content-Type': 'application/json' };

		const requestPromise = client({
			method,
			url: path,
			headers: { ...axios.defaults.headers, ...optional },
			data: !(['GET', 'DELETE'].includes(method)) ? JSON.stringify(data) : undefined
		}).finally(() => pendingRequests.delete(key));

		pendingRequests.set(key, requestPromise);

		return requestPromise;
	}
}


// ---------------- Token Handling ----------------

const refreshAccessToken = async () => {

	if (tokenPromise) return tokenPromise;

	tokenPromise = (async () => {

		try {

			const res = await DataServicePYV2.post('/core/getAccessToken', { id: 'sharpely-app' });

			if (res?.type) {
				accessToken = res.data?.access_token;
				return accessToken;
			}

			accessToken = null;
			return '';

		} catch {
			accessToken = null;
			return '';
		}
		finally {
			tokenPromise = null;
		}

	})();

	return tokenPromise;
};

const getAccessToken = async () =>
	accessToken || await refreshAccessToken();


// ---------------- Request Interceptor ----------------
client.interceptors.request.use(
	async config => {

		if (config?.url?.includes('/core/getAccessToken')) return config;

		const token = await getAccessToken();

		if (token) config.headers.Authorization = `Bearer ${token}`;

		return config;

	},
	error => Promise.reject(error)
);


// ---------------- Response Interceptor ----------------
client.interceptors.response.use(

	async response => {

		let data = response.data;

		try {
			data = await crypto.decryptAES(data);
		}
		catch {
			data = response.data;
		}

		try {
			data = typeof data === 'string'
				? base64regex.test(data)
					? String(data)
					: data.replace(/NaN/g, null).replace(/-Infinity/g, null).replace(/Infinity/g, null)
				: data;

			if (typeof data === "string") {
				try {
					const inner = JSON.parse(data);
					data = inner;
				} catch { }
			}
		}
		catch { }

		if (response.status === 200)
			return { msg: 'Success', data, type: true };

		return { msg: response.statusText, data, type: false };
	},

	async error => {

		const { response } = error;
		const originalRequest = error.config;

		if (response) {

			const isAuthError = response.status === 403 || response.status === 502;
			const isTokenAPI = originalRequest?.url?.includes('/core/getAccessToken');

			// ✅ Retry only ONCE
			if (isAuthError && !originalRequest._retry && !isTokenAPI) {

				originalRequest._retry = true;

				// ✅ IMPORTANT: clear old token first
				accessToken = null;

				try {
					const newToken = await refreshAccessToken();

					if (newToken) {
						originalRequest.headers.Authorization = `Bearer ${newToken}`;
					}

					return client(originalRequest);
				} catch {
					// if refresh fails → fall through
				}
			}

			// ❌ No retry → return error as-is
			return {
				msg: response?.data?.['api-status'] || 'Failed',
				data: typeof response?.data === 'string'
					? base64regex.test(response.data)
						? String(response.data)
						: response.data.replace(/NaN/g, null).replace(/-Infinity/g, null).replace(/Infinity/g, null)
					: response?.data,
				type: false
			};
		}

		return Promise.reject(error);
	}
);

export { DataServicePYV2 };